/**
 * Job Post Lifecycle Status Helpers
 *
 * Enforces mutual exclusivity and lifecycle priority:
 * 1. Closed: isClosed === "1" | 1 (Highest priority - terminal/inactive state)
 * 2. On Hold: isSuspended === "1" | 1 (Suspended by employer/admin)
 * 3. Draft: is_draft === "1" | 1 (Unpublished draft)
 * 4. Published / Active: default (no lifecycle badge)
 */

export const isJobClosed = (job) => {
  return Boolean(job && (job.isClosed === 1 || job.isClosed === "1"));
};

export const isJobSuspended = (job) => {
  return Boolean(job && (job.isSuspended === 1 || job.isSuspended === "1"));
};

export const isJobDraft = (job) => {
  return Boolean(job && (job.is_draft === 1 || job.is_draft === "1"));
};

/**
 * Resolves the single, mutually exclusive lifecycle badge for a job card.
 * If a job is both suspended and closed, Closed takes higher priority.
 *
 * @param {Object} job
 * @returns {{ key: string, label: string, backgroundColor: string, color: string } | null}
 */
export const getJobStatusBadge = (job) => {
  if (!job) return null;

  // Priority 1: Closed
  if (isJobClosed(job)) {
    return {
      key: "closed",
      label: "Closed",
      backgroundColor: "#dc3545",
      color: "#fff",
    };
  }

  // Priority 2: On Hold
  if (isJobSuspended(job)) {
    return {
      key: "hold",
      label: "On Hold",
      backgroundColor: "#ffc107",
      color: "#000",
    };
  }

  // Priority 3: Draft
  if (isJobDraft(job)) {
    return {
      key: "draft",
      label: "Draft",
      backgroundColor: "#6c757d",
      color: "#fff",
    };
  }

  return null;
};

/**
 * Returns human-readable status label for selectors and lists.
 * Priority: Closed > On Hold > Draft > Published
 *
 * @param {Object} job
 * @returns {string}
 */
export const getJobStatusLabel = (job) => {
  if (!job) return "Published";
  if (isJobClosed(job)) return "Closed";
  if (isJobSuspended(job)) return "On Hold";
  if (isJobDraft(job)) return "Draft";
  return "Published";
};

/**
 * Returns CSS class for status indicators in selectors and tables.
 * Priority: status-closed > status-hold > status-draft > status-published
 *
 * @param {Object} job
 * @returns {string}
 */
export const getJobStatusClass = (job) => {
  if (!job) return "status-published";
  if (isJobClosed(job)) return "status-closed";
  if (isJobSuspended(job)) return "status-hold";
  if (isJobDraft(job)) return "status-draft";
  return "status-published";
};

/**
 * Checks whether a job matches a given status filter, respecting priority rules.
 *
 * @param {Object} job
 * @param {string} filterStatus
 * @returns {boolean}
 */
export const matchesJobStatusFilter = (job, filterStatus) => {
  if (!job || !filterStatus || filterStatus === "all") return true;

  const closed = isJobClosed(job);
  const suspended = isJobSuspended(job);
  const draft = isJobDraft(job);

  switch (filterStatus) {
    case "active":
      return !closed && !suspended && !draft;
    case "hold":
      // Closed takes priority over hold; closed jobs cannot match hold
      return suspended && !closed && !draft;
    case "closed":
      return closed && !draft;
    case "draft":
      return draft;
    case "published":
      return !draft;
    default:
      return true;
  }
};
